/**
 * PlatformLayout.jsx — Clean white layout zoals rankingCoach
 */
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Search, Target, Link2, Settings, Zap, LogOut,
  Menu, Bell, ChevronDown, Users, Building, Crown, Sparkles, Globe, Clock,
  TrendingUp, Briefcase, Calendar, ShieldCheck,
} from 'lucide-react';

const NAV_ITEMS = [
  { href: '/platform', icon: LayoutDashboard, label: 'Dashboard' },
  { href: '/platform/opdrachten', icon: Zap, label: 'Verbeterpunten', badge: 'AI' },
  { href: '/platform/ai-acties', icon: Sparkles, label: 'AI Acties', badge: 'NIEUW' },
  { href: '/platform/backlinks', icon: TrendingUp, label: 'Backlinks', badge: 'LIVE' },
  { href: '/platform/integraties', icon: Link2, label: 'Integraties' },
  {
    href: '/platform/seo', icon: Search, label: 'SEO & Vindbaarheid',
    sub: [
      { href: '/platform/seo/zoekwoorden', label: 'Zoekwoorden' },
      { href: '/platform/seo/lokaal', label: 'Lokale Marketing' },
      { href: '/platform/seo/analytics', label: 'Web Analytics' },
      { href: '/platform/profiel', label: 'Google Bedrijfsprofiel' },
      { href: '/platform/reviews', label: 'Reviews' },
    ]
  },
  {
    href: '/platform/google-ads', icon: Target, label: 'Advertenties',
    sub: [
      { href: '/platform/google-ads', label: 'Google Ads' },
      { href: '/platform/meta-ads', label: 'Meta Ads' },
      { href: '/platform/tiktok-ads', label: 'TikTok Ads' },
    ]
  },
  {
    href: '/platform/website-bouw', icon: Globe, label: 'Website',
    sub: [
      { href: '/platform/website-bouw', label: 'Bouw / Redesign' },
      { href: '/platform/versies', label: 'Versie historie' },
    ]
  },
  { href: '/platform/concurrenten', icon: Users, label: 'Concurrenten' },
  { href: '/platform/beheer', icon: Crown, label: 'AI Beheer', badge: 'Premium' },
  {
    href: '/platform/instellingen', icon: Settings, label: 'Instellingen',
    sub: [
      { href: '/platform/instellingen/bedrijf', label: 'Bedrijfsinformatie' },
      { href: '/platform/billing-info', label: 'Mijn factuurgegevens' },
      { href: '/platform/instellingen/account', label: 'Account' },
      { href: '/platform/facturen', label: 'Mijn facturen' },
      { href: '/platform/website-chat', label: 'Website laten bouwen (AI)' },
      { href: '/platform/website-aanvraag', label: 'Mijn website-aanvragen' },
    ]
  },
];

const NavItem = ({ item, location, collapsed, setCollapsed }) => {
  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');
  const active = isActive(item.href);
  const groupActive = item.sub?.some(s => isActive(s.href));
  const isOpen = collapsed[item.label] !== false; // default open

  const Icon = item.icon;

  if (item.sub) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Link to={item.href}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: '10px',
              padding: '9px 12px', borderRadius: '8px', textDecoration: 'none',
              fontSize: '13px', fontWeight: (active || groupActive) ? 600 : 500,
              color: (active || groupActive) ? '#0f172a' : '#64748b',
              background: active ? '#f1f5f9' : 'transparent',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f8fafc'; }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
            <Icon style={{ width: '16px', height: '16px', flexShrink: 0 }} />
            {item.label}
            {item.badge && (
              <span style={{ marginLeft: 'auto', background: '#D4A017', color: '#0f172a', fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '10px' }}>
                {item.badge}
              </span>
            )}
          </Link>
          <button onClick={() => setCollapsed(c => ({ ...c, [item.label]: !isOpen }))}
            style={{ padding: '9px 8px', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
            <ChevronDown style={{ width: '13px', height: '13px', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
        </div>
        {isOpen && (
          <div style={{ marginLeft: '26px', borderLeft: '2px solid #f1f5f9', paddingLeft: '10px', marginTop: '2px' }}>
            {item.sub.map(s => (
              <Link key={s.href} to={s.href}
                style={{
                  display: 'block', padding: '7px 10px', borderRadius: '6px', textDecoration: 'none',
                  fontSize: '12px', fontWeight: isActive(s.href) ? 600 : 400,
                  color: isActive(s.href) ? '#D4A017' : '#64748b',
                  background: isActive(s.href) ? 'rgba(212,160,23,0.08)' : 'transparent',
                  transition: 'all 0.15s', marginBottom: '1px',
                }}
                onMouseEnter={e => { if (!isActive(s.href)) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { if (!isActive(s.href)) e.currentTarget.style.background = 'transparent'; }}>
                {s.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link to={item.href}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '9px 12px', borderRadius: '8px', textDecoration: 'none',
        fontSize: '13px', fontWeight: active ? 600 : 500,
        color: active ? '#0f172a' : '#64748b',
        background: active ? '#f1f5f9' : 'transparent',
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f8fafc'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
      <Icon style={{ width: '16px', height: '16px', flexShrink: 0, color: active ? '#D4A017' : 'inherit' }} />
      {item.label}
      {item.badge && (
        <span style={{ marginLeft: 'auto', background: '#D4A017', color: '#0f172a', fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '10px' }}>
          {item.badge}
        </span>
      )}
    </Link>
  );
};

export default function PlatformLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const Sidebar = () => (
    <aside style={{ width: '220px', background: 'white', borderRight: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', height: '100vh', overflowY: 'auto', flexShrink: 0 }}>
      {/* Logo */}
      <div style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '30px', height: '30px', background: '#0f172a', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ color: '#D4A017', fontSize: '14px', fontWeight: 700 }}>♛</span>
        </div>
        <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>IMPERIA AI</span>
      </div>

      {/* Bedrijf selector */}
      <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: '#f8fafc', borderRadius: '8px', cursor: 'pointer' }}>
          <Building style={{ width: '14px', height: '14px', color: '#64748b' }} />
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.name || 'Mijn bedrijf'}
          </span>
          <ChevronDown style={{ width: '12px', height: '12px', color: '#94a3b8', flexShrink: 0 }} />
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '8px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {/* Team-rollen: top entry naar team-dashboard */}
          {['projectmanager', 'partner', 'teamleider', 'agent'].includes(user?.role) && (
            <NavItem
              item={{ href: '/team/dashboard', icon: Briefcase, label: 'Team dashboard', badge: 'NIEUW' }}
              location={location}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
            />
          )}
          {/* Partner / affiliate intake-plannen entry */}
          {['projectmanager', 'partner', 'teamleider', 'agent', 'affiliate'].includes(user?.role) && (
            <NavItem
              item={{ href: '/platform/intake-plannen', icon: Calendar, label: 'Intake plannen', badge: 'NIEUW' }}
              location={location}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
            />
          )}
          {/* PM-eigen Calendly link beheren */}
          {['projectmanager', 'admin', 'manager'].includes(user?.role) && (
            <NavItem
              item={{ href: '/platform/mijn-calendly', icon: Calendar, label: 'Mijn Calendly', badge: 'NIEUW' }}
              location={location}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
            />
          )}
          {/* 2FA settings — admin/manager/projectmanager */}
          {['projectmanager', 'admin', 'manager'].includes(user?.role) && (
            <NavItem
              item={{ href: '/platform/2fa', icon: ShieldCheck, label: 'Twee-staps verificatie', badge: 'NIEUW' }}
              location={location}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
            />
          )}
          {/* Affiliate-rol: dashboard */}
          {(user?.role === 'affiliate' || user?.is_affiliate) && (
            <NavItem
              item={{ href: '/affiliate', icon: Sparkles, label: 'Affiliate', badge: '20%' }}
              location={location}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
            />
          )}
          {NAV_ITEMS.map(item => (
            <NavItem key={item.href} item={item} location={location} collapsed={collapsed} setCollapsed={setCollapsed} />
          ))}
        </div>
      </nav>

      {/* Upgrade CTA */}
      {user?.role === 'radar_free' && (
        <div style={{ margin: '8px', background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', borderRadius: '12px', padding: '14px' }}>
          <p style={{ color: '#D4A017', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Upgrade</p>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px', marginBottom: '10px' }}>Koppel uw accounts voor slimmere AI-tips</p>
          <Link to="/platform/checkout" style={{ display: 'block', textAlign: 'center', background: '#D4A017', color: '#0f172a', fontWeight: 700, fontSize: '12px', padding: '8px', borderRadius: '8px', textDecoration: 'none' }}>
            €14,95 excl. btw
          </Link>
        </div>
      )}

      {/* Logout */}
      <div style={{ padding: '8px', borderTop: '1px solid #f1f5f9' }}>
        <button onClick={() => { logout(); navigate('/login'); }}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '8px', border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '12px', transition: 'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#ef4444'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#94a3b8'; }}>
          <LogOut style={{ width: '14px', height: '14px' }} />
          Uitloggen
        </button>
      </div>
    </aside>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: '#f8fafc', fontFamily: "'Inter', sans-serif" }}>
      {/* Desktop sidebar */}
      <div className="hidden lg:flex">
        <Sidebar />
      </div>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50 }} className="lg:hidden">
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setMobileOpen(false)} />
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '220px' }}>
            <Sidebar />
          </div>
        </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', overflow: 'hidden' }}>
        {/* Topbar */}
        <header style={{ height: '56px', background: 'white', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', flexShrink: 0 }}>
          <button className="lg:hidden" onClick={() => setMobileOpen(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
            <Menu size={20} />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
            {/* Jump Start button */}
            <Link to="/platform/integraties"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#0f172a', color: 'white', border: 'none', borderRadius: '20px', padding: '7px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}>
              <Zap style={{ width: '13px', height: '13px', color: '#D4A017' }} />
              <span className="hidden sm:inline">Verbind accounts</span>
            </Link>

            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '6px' }}>
              <Bell size={17} />
            </button>

            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #D4A017, #f5c842)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '13px', color: '#0f172a', cursor: 'pointer' }}>
              {(user?.name || 'U')[0].toUpperCase()}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="platform-content" style={{ flex: 1, padding: '24px', overflow: 'auto', maxWidth: '1200px', width: '100%', margin: '0 auto' }}>
          {children}
        </main>
      </div>
    </div>
  );
}
